import { useMemo, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';

import { profileApi, eligibleFeaturesApi, ProfileModel } from '@pukka/api';
import { assets } from '@pukka/common';
import { animationUtil, hookUtils } from '@pukka/common/utils';
import { Button, Icon } from '@pukka/ui-components';
import { useFlagWithFallback, useVariantWithFallback } from '@pukka/unleash';
import * as Accordion from '@radix-ui/react-accordion';
import { AiOutlineMenu } from 'react-icons/ai';
import { twMerge } from 'tailwind-merge';

import { SidebarFooter } from './SidebarFooter';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarProfileButton } from './SidebarProfileButton';

import './style.module.css';

function Sidebar() {
  const location = useLocation();
  const isMobile = hookUtils.useIsMobile();

  const [menuOpen, setMenuOpen] = useState(location.pathname.split('/')[2] || '');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { data: profile } = profileApi.useProfile();
  const eligibleFeaturesData = eligibleFeaturesApi.useEligibleFeatures();
  const [bundleReportRedDot] = useFlagWithFallback('backoffice-bundle-report-red-dot');
  const [bundlePackageRedDot] = useFlagWithFallback('bundle-package-red-dot-ui');
  const [ecommerceAttributes] = useFlagWithFallback('ecommerce-attributes');
  const [enableGobizOutletLinking] = useFlagWithFallback('enable-gobiz-outlet-linking');
  const [pukkaRedirection] = useVariantWithFallback('backoffice-pukka-redirection');

  const pukkaMenus: Record<string, string> | undefined = useMemo(() => {
    return pukkaRedirection?.enabled && pukkaRedirection.payload?.type === 'json' && JSON.parse(pukkaRedirection.payload.value);
  }, [pukkaRedirection]);

  const menuWithRedDots = useMemo(
    () => [
      {
        menu: 'Reports',
        subMenu: 'Sales',
        enabled: bundleReportRedDot,
      },
      {
        menu: 'Library',
        subMenu: 'Bundle Package',
        enabled: bundlePackageRedDot,
      },
      {
        menu: 'Account Settings',
        subMenu: 'Outlets',
        enabled:
          enableGobizOutletLinking &&
          profile?.outlets?.some((outlet) => {
            return outlet.saudagar != null && outlet.saudagar.status === 'deactive';
          }),
      },
    ],
    [bundleReportRedDot, bundlePackageRedDot, enableGobizOutletLinking, profile],
  );

  // Grouping submenus to each root menu and do sort and some filtering as well
  const menus = useMemo(() => {
    return [...(profile?.permissions || [])]
      ?.sort((a, b) => (a?.priority_rank || 0) - (b?.priority_rank || 0))
      ?.filter((menu) => menu.access === 'Back-office')
      .reduce(
        (acc, value) => {
          // Adding related red dots to the permission object
          menuWithRedDots.forEach((menu) => {
            if (menu.subMenu === value.sub_menu) {
              value.withRedDot = Boolean(menu.enabled);
            }
          });

          // Index 0 = LEVEL 2 permission, we can only adjust up to level 2 permission
          // So, using index 0 is fine
          if (value.urls?.[0] && pukkaMenus?.[value.urls[0]]) {
            value.urls[0] = pukkaMenus[value.urls[0]];
            value.isPukka = true;
          }

          // Hide Brand Inventory if toggle is on (this comment is copied from legacy)
          if (!eligibleFeaturesData.includes('ECOMMERCE_ATTRIBUTES') || !ecommerceAttributes) {
            if (value.menu === 'Library' && value.sub_menu === 'Brands') {
              value.isHidden = true;
            }

            if (value.menu === 'Outlet Library' && value.sub_menu === 'Brands') {
              value.isHidden = true;
            }
          }

          acc[value.menu || ''] = acc[value.menu || ''] || [];
          acc[value.menu || ''].push(value);

          return acc;
        },
        {} as { [key: string]: ProfileModel.Permission[] },
      );
  }, [profile, menuWithRedDots, ecommerceAttributes, eligibleFeaturesData, pukkaMenus]);

  const toggleSidebar = useCallback(() => {
    animationUtil.doTransition(() => {
      flushSync(() => setSidebarOpen(!sidebarOpen));
    });
  }, [sidebarOpen]);

  return (
    <div className={isMobile ? 'w-full' : ''}>
      {isMobile && (
        <header className="flex h-14 w-full items-center justify-between bg-legacy-500 px-4">
          <Link to="/" reloadDocument data-testid="moka-logo-mobile">
            <img alt="Moka logo" width={40} height={40} src={assets.logoMoka} />
          </Link>

          <button className="p-1.5" type="button" aria-label="menu button" data-testid="menu-button" onClick={toggleSidebar}>
            <AiOutlineMenu size={24} className="text-neutral-300" />
          </button>
        </header>
      )}

      <aside
        role="navigation"
        data-testid="sidebar"
        className={twMerge(
          'z-50 flex h-full w-sidebar flex-col bg-legacy-800 text-white',
          isMobile && 'view-transition-[sidebar]',
          isMobile && 'absolute right-0 top-0',
          isMobile && !sidebarOpen ? 'hidden' : '',
        )}
      >
        <div className="flex h-[65px] flex-row items-center justify-between bg-legacy-600 pl-[30px] pr-3">
          <Link to="/" reloadDocument data-testid="moka-logo" className="p-nav-item pl-0">
            <img alt="Moka logo" width={103} height={22} src={assets.logoMokaFull} className="relative h-[22px]" />
          </Link>
          {isMobile ? (
            <Button variant="nude" onClick={toggleSidebar}>
              <Icon icon="close" size="xl" color="white" className="opacity-40" />
            </Button>
          ) : null}
        </div>
        <SidebarProfileButton />

        <nav className="flex h-1 flex-1 flex-col overflow-auto">
          <ul>
            <Accordion.Root type="single" collapsible value={menuOpen} onValueChange={setMenuOpen}>
              {menus &&
                Object.keys(menus).map((key) => (
                  <Accordion.Item value={key.toLowerCase()} key={key}>
                    <SidebarNavItem name={key} key={key} menus={menus} onClickSubMenu={toggleSidebar} />
                  </Accordion.Item>
                ))}
            </Accordion.Root>
          </ul>
        </nav>

        <SidebarFooter />
      </aside>
      {sidebarOpen && isMobile && (
        <div
          role="presentation"
          onClick={toggleSidebar}
          data-testid="sidebar-overlay"
          className="fixed inset-0 z-40 bg-black/70 view-transition-[overlay]"
        />
      )}
    </div>
  );
}

export { Sidebar };
